// **Die Worte zu den Schlüsseln des Servers.** Der Server schickt Codes
// (Zustände, Schritte, Prüfungen, Fehler), die Oberfläche besitzt die Sätze —
// dasselbe Muster wie `stepLabel()` im SoluCoder-Client. Ein neuer Schritt in
// `src/runs.ts` braucht hier einen Eintrag, sonst steht sein Schlüssel da.

import { ApiError } from "../api/client";

/** Die Tönung eines Abzeichens oder Hinweises (`pf-badge--<tone>`); leer = neutral. */
export type Tone = "" | "ok" | "info" | "warn" | "error";

export const STATE: Record<string, [string, Tone]> = {
  draft: ["Entwurf", ""],
  provisioning: ["Wird eingerichtet", "info"],
  active: ["Aktiv", "ok"],
  failed: ["Fehler", "error"],
  suspended: ["Angehalten", "warn"],
  deleting: ["Wird gelöscht", "warn"],
};

export const STEP: Record<string, string> = {
  dns: "DNS-Einträge bei Hetzner",
  database: "Datenbank und DB-Benutzer",
  appwrite: "Appwrite: Team und Service-Konto",
  platform_key: "Requesty: Key mit Monatslimit",
  flow_key: "Requesty: Key der Flow-Modelle mit eigenem Limit",
  files: ".env und docker-compose.yml schreiben, Images ziehen",
  migrate: "Migrationen (neues Image, vor dem Start)",
  start: "Instanz starten, Gesundheit und Zertifikat abwarten",
  configure: "Lizenz, Endpoints, Integrationswerte und Flow-Modelle einspielen",
  first_admin: "Ersten Tenant-Admin anlegen und einladen",
  restart_if_changed: "Neustart im Wartungsfenster (nur bei geänderter .env)",
  restart: "Neustart im Wartungsfenster",
  rollout: "Rollout auf einen Build: alle Images ziehen, dann im Wartungsfenster .env, Migration, Start",
  maintenance: "Wartungsmodus umschalten",
  stop: "Instanz anhalten",
  remove_instance: "Instanz entfernen (Container, Volumes, Netze)",
  remove_files: "Verzeichnis /opt/tenants/<slug> entfernen",
  platform_key_delete: "Requesty-Key löschen",
  flow_key_delete: "Requesty-Key der Flow-Modelle löschen",
  feedback: "Feedback-API: Schlüssel und Eintrag",
  feedback_disable: "Feedback-API: abschalten",
  appwrite_remove: "Appwrite: Projekte, Team und Konto löschen",
  database_drop: "Datenbank und DB-Benutzer löschen",
  dns_remove: "DNS-Einträge entfernen",
};

export const RUN_KIND: Record<string, string> = {
  setup: "Einrichtung",
  teardown: "Rückbau (Abbrechen)",
  apply: "Einstellungen übernehmen",
  rollout: "Rollout",
  restart: "Neustart",
  maintenance: "Wartung",
  suspend: "Anhalten",
  resume: "Fortsetzen",
  delete: "Löschen",
};

export const RUN_STATE: Record<string, [string, Tone]> = {
  running: ["läuft", "info"],
  failed: ["gescheitert", "error"],
  done: ["fertig", "ok"],
  cancelled: ["abgebrochen", ""],
};

/** Prüfcodes aus den Wizard-Schritten (tenants.ts) und den Proben (probes.ts). */
export const CHECK: Record<string, string> = {
  required: "Pflichtfeld",
  email: "Keine gültige E-Mail-Adresse",
  empty: "Mindestens eins auswählen",
  syntax: "Nur a–z, 0–9 und Bindestrich; nicht am Anfang oder Ende; höchstens 32 Zeichen",
  double_dash: "Kein doppelter Bindestrich (reserviert für die App-Vorschau)",
  reserved: "Dieser Name ist reserviert",
  taken: "Dieser Slug ist schon vergeben",
  dns_taken: "Im DNS gibt es schon einen Eintrag mit diesem Namen",
  dns_unchecked: "DNS konnte nicht geprüft werden (Provisioner nicht erreichbar) — beim Einrichten wird noch einmal geprüft",
  past: "Liegt in der Vergangenheit",
  guid: "Keine GUID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)",
  https: "Muss mit https:// beginnen",
  positive: "Muss größer als 0 sein",
  none: "Mindestens ein Endpoint",
  flow_models_empty: "Mindestens ein Flow-Modell",
  not_enabled: "Der Standard muss eingeschaltet sein",
  platform_keys_off: "Plattform-Keys sind aus (REQUESTY_MANAGEMENT_KEY fehlt)",
  requires_entra: "Braucht Entra als Anmeldeverfahren",
  memory_over_half_host: "Speicher je Arbeitsbereich ist mehr als die Hälfte des Hosts",
  cpus_over_host: "Mehr CPUs je Arbeitsbereich, als der Host hat",
  workspace_unlimited: "Arbeitsbereiche ohne Grenze auf einem geteilten Host",
  unknown_template: "Eine gewählte Lizenz-Vorlage gibt es nicht mehr",
  seat_count: "Jede gewählte Lizenz braucht mindestens einen Platz",
  seats_over_users: "Mehr Lizenz-Plätze als Benutzer erlaubt",
  host_unknown: "Kapazität des Hosts unbekannt (Provisioner nicht erreichbar)",
  local_needs_smtp: "Lokale Konten brauchen SMTP (TENANT_SMTP_* der Plattform) — sonst kommt der Einladungslink hier zum Kopieren",
  // Proben
  ok: "In Ordnung",
  client_recognized: "Client erkannt (der Anbieter erlaubt Client-Credentials nicht, die Zugangsdaten stimmen)",
  issuer_not_https: "Issuer muss mit https:// beginnen",
  discovery_unreachable: "Discovery nicht erreichbar",
  discovery_failed: "Discovery antwortet nicht mit einer Konfiguration",
  discovery_incomplete: "Discovery ohne authorization_endpoint oder token_endpoint",
  issuer_mismatch: "Der Issuer der Discovery weicht ab",
  client_missing: "Client-ID oder Secret fehlt",
  token_unreachable: "Token-Endpoint nicht erreichbar",
  invalid_client: "Client-ID oder Secret falsch",
  unknown_client: "Die Client-ID ist in diesem Tenant unbekannt",
  unknown_tenant: "Diesen Entra-Tenant gibt es nicht",
  token_failed: "Token-Endpoint lehnt ab",
  base_url_invalid: "Adresse ungültig",
  gateway_failed: "Gateway antwortet nicht mit einer Modellliste",
  gateway_empty: "Gateway führt keine Modelle",
  default_not_in_catalog: "Das Standardmodell steht nicht im Katalog des Gateways",
};

export function checkText(code: string): string {
  if (code.startsWith("internal:")) return `Solutec-intern: ${code.slice(9)} steht Kundenmandanten nicht zur Verfügung`;
  return CHECK[code] ?? code;
}

export const ERROR: Record<string, string> = {
  unauthorized: "Nicht angemeldet",
  origin_mismatch: "Anfrage von fremder Herkunft abgewiesen",
  tenant_not_found: "Mandant nicht gefunden",
  slug_taken: "Dieser Slug ist schon vergeben",
  invalid: "Eingaben unvollständig",
  incomplete: "Nicht alle Schritte sind vollständig",
  invalid_state: "Im aktuellen Zustand nicht möglich",
  busy: "Für diesen Mandanten läuft schon etwas",
  use_retry: "Die Einrichtung ist gescheitert — „Erneut versuchen“ oder „Abbrechen“",
  not_cancellable: "Nur eine gescheiterte Einrichtung lässt sich abbrechen",
  confirm_mismatch: "Der eingetippte Slug stimmt nicht",
  instance_error: "Die Instanz antwortet nicht wie erwartet",
  invite_gone: "Der Einladungslink wurde schon angezeigt",
  invalid_image_tag: "Ungültiger Image-Tag",
  invalid_build: "Kein gültiger Build",
  unknown_build: "Diesen Build gibt es nicht (oder er hat keinen vollständigen Satz Images)",
  runs_running: "Es läuft noch ein Lauf — die Aktualisierung startet die Plattform neu und bräche ihn ab",
  update_running: "Die Plattform wird gerade aktualisiert",
  provisioner_unreachable: "Provisioner nicht erreichbar",
  images_missing: "Nicht alle Images des Builds sind verfügbar — es wurde nichts verändert",
  no_tenants: "Kein Mandant ausgewählt",
  target_missing: "Kein Benutzer gewählt",
  internal: "Interner Fehler",
};

/** Der Satz zu einem Fehler — ein `ApiError` über seinen Code, sonst seine Meldung. */
export function errorText(err: unknown): string {
  if (!err) return "Unbekannter Fehler";
  if (err instanceof ApiError) {
    if (err.code === "instance_error" && err.body) return `${ERROR.instance_error} (${err.body.status || "–"} ${err.body.code || ""})`;
    return ERROR[err.code] ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

export const FEATURE: Record<string, string> = { chat: "Chat", projects: "Arbeitsbereiche", tasks: "Aufgaben", agents: "Agents" };
export const ENDPOINT_KIND: Record<string, string> = { pi: "pi (eigener Agent)", claude_code: "Claude Code (am Gateway)", codex: "Codex", goose: "Goose" };
export const AUTH_MODE: Record<string, [string, string]> = {
  entra: ["Microsoft Entra", "App-Registrierung des Kunden; nur damit gibt es M365, GDAP, SharePoint und Dynamics"],
  oidc: ["OIDC", "Ein beliebiger OpenID-Connect-Anbieter"],
  local: ["Lokale Konten", "E-Mail und Passwort in der Instanz, Einladung per Mail"],
};

export const PLATFORM_EVENT: Record<string, string> = {
  TENANT_MODEL_CHANGED: "Modell geändert (Profil oder Preis)",
  LICENSE_TEMPLATE_CREATED: "Lizenz-Vorlage angelegt",
  FEEDBACK_STATUS_CHANGED: "Feedback: Status gesetzt",
  FEEDBACK_COMMENTED: "Feedback kommentiert",
  LICENSE_TEMPLATE_UPDATED: "Lizenz-Vorlage geändert",
  LICENSE_TEMPLATE_DELETED: "Lizenz-Vorlage gelöscht",
  TENANT_USER_ADDED: "Benutzer hinzugefügt",
  TENANT_TEMPLATES_CHANGED: "Vorlagen geändert",
  PLATFORM_LOGIN: "Anmeldung an der Plattform",
  PLATFORM_LOGOUT: "Abmeldung",
  PLATFORM_LOGIN_DENIED: "Anmeldung abgewiesen",
  TENANT_DRAFT_CREATED: "Entwurf angelegt",
  TENANT_DRAFT_SAVED: "Entwurf gespeichert",
  TENANT_DRAFT_DISCARDED: "Entwurf verworfen",
  TENANT_SETUP_STARTED: "Einrichtung gestartet",
  TENANT_SETUP_RETRIED: "Lauf wiederholt",
  TENANT_SETUP_CANCELLED: "Einrichtung abgebrochen",
  TENANT_SETTINGS_CHANGED: "Einstellungen geändert",
  TENANT_CONFIG_APPLIED: "Einstellungen übernommen",
  TENANT_LICENSE_UPDATED: "Lizenz aktualisiert",
  TENANT_ADMIN_LOGIN: "In Mandant angemeldet",
  TENANT_IMPERSONATION: "Benutzer impersoniert",
  TENANT_MAINTENANCE: "Wartung umgeschaltet",
  TENANT_RESTART: "Neustart",
  TENANT_ROLLOUT: "Rollout",
  TENANT_SUSPENDED: "Angehalten",
  TENANT_RESUMED: "Fortgesetzt",
  TENANT_DELETED: "Gelöscht",
  PLATFORM_UPDATE: "Plattform aktualisiert",
};

export const WIZARD_STEPS: readonly string[] = ["Kunde", "Lizenz", "Anmeldung", "Modelle", "Integrationen & Skills", "Erster Tenant-Admin", "Zusammenfassung"];

/** Zustand eines Mandanten als Wort und Tönung — Unbekanntes steht als Schlüssel da. */
export function stateOf(state: string): [string, Tone] {
  return STATE[state] ?? [state, ""];
}

/** Zustand eines Laufs als Wort und Tönung. */
export function runStateOf(state: string): [string, Tone] {
  return RUN_STATE[state] ?? [state, ""];
}
